/**
 * Simulation Studio
 *
 * Business-task-level optimization via Fast Replay.
 * 1. Business task list — real data from /v2/data/services/{id}/workflows
 * 2. Agent LLM operation editor — real data from /v2/data/services/{id}/workflows/{id}/llm-operations
 * 3. Override editor table (per agent/LLM operation)
 * 4. Run simulation → POST /v2/data/services/{id}/workflows/{id}/replay-runs → real before/after metrics
 * 5. Run history — real data from /v2/data/services/{id}/workflows/{id}/replay-runs
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useStytchMember } from '@stytch/react/b2b';
import { useSessionJwt } from '../../hooks/useSessionJwt';
import { useTimeRange } from '../../contexts/TimeRangeContext';
import {
  ArrowLeft, DollarSign, Clock, Sparkles, Play,
  ChevronDown, ChevronRight,
  RotateCcw, TrendingDown, TrendingUp, Minus,
  AlertTriangle, Info, ExternalLink, Copy, Search, X
} from 'lucide-react';
import { palette, components as ds, withOpacity } from '../../utils/designTokens';
import { v2ServicePath } from '../../config';
import SpanInspector from '../../components/SpanInspector';
import {
  getTableCellColor,
  getTableRowStyle,
  tableGridHeaderClassName,
} from '../../components/TablePrimitives';

// Admin-role check — mirrors Settings.js's hasAdminRole so "is this user an
// admin" reads the same way everywhere Stytch member roles are consulted.
const ADMIN_ROLE_IDS = new Set(['stytch_admin', 'admin']);

const normalizeRoleValues = (rawRoles) => {
  if (!rawRoles) return [];
  if (Array.isArray(rawRoles)) {
    return rawRoles
      .map((role) => {
        if (typeof role === 'string') return role.trim().toLowerCase();
        if (role && typeof role === 'object') return String(role.role_id || role.role || '').trim().toLowerCase();
        return '';
      })
      .filter(Boolean);
  }
  if (typeof rawRoles === 'string') {
    return rawRoles.split(',').map((role) => role.trim().toLowerCase()).filter(Boolean);
  }
  return [];
};

const hasAdminRole = (rawRoles) => normalizeRoleValues(rawRoles).some((role) => ADMIN_ROLE_IDS.has(role));

// ─────────────────────────────────────────────────────────────────────────────
// StyledSelect — custom dropdown matching header selector style
// ─────────────────────────────────────────────────────────────────────────────
const StyledSelect = ({ value, onChange, options, placeholder = 'Select…', style = {}, searchable = false, searchPlaceholder = 'Search…' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuPosition, setMenuPosition] = useState(null);
  const ref = React.useRef(null);
  const menuRef = React.useRef(null);

  const updateMenuPosition = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const maxHeight = 260;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const opensUp = spaceBelow < 180 && spaceAbove > spaceBelow;
    const availableHeight = Math.max(120, Math.min(maxHeight, opensUp ? spaceAbove - 4 : spaceBelow - 4));
    setMenuPosition({
      top: opensUp ? Math.max(8, rect.top - availableHeight - 4) : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      maxHeight: availableHeight,
    });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (
        (ref.current && ref.current.contains(e.target)) ||
        (menuRef.current && menuRef.current.contains(e.target))
      ) {
        return;
      }
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  const selected = options.find(o => o.value === value);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = normalizedQuery
    ? options.filter(opt => `${opt.label} ${opt.value}`.toLowerCase().includes(normalizedQuery))
    : options;

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <button
        type="button"
        onClick={() => {
          if (!open) updateMenuPosition();
          setOpen(v => {
            if (v) setQuery('');
            return !v;
          });
        }}
        className="flex items-center justify-between w-full"
        style={{
          ...ds.select,
          width: '100%',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ color: palette.text.primary, fontSize: 13 }}>
          {selected ? selected.label : value || placeholder}
        </span>
        <ChevronDown size={12} style={{ color: palette.text.muted, flexShrink: 0, marginLeft: 6, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
      </button>

      {open && menuPosition && createPortal(
        <div
          ref={menuRef}
          className="overflow-y-auto"
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width,
            zIndex: 10000,
            backgroundColor: palette.neutrals.panelSecondary,
            border: `1px solid ${palette.neutrals.borderStrong}`,
            borderRadius: '0.75rem',
            boxShadow: ds.dropdown?.container?.shadow || palette.quality.shadowCard,
            maxHeight: menuPosition.maxHeight,
            padding: '4px',
          }}
        >
          {searchable && (
            <div
              className="flex items-center gap-1.5"
              style={{
                padding: '6px 8px',
                marginBottom: 4,
                borderRadius: 8,
                border: `1px solid ${palette.borders.subtle}`,
                backgroundColor: palette.neutrals.surfaceSubtle,
              }}
            >
              <Search size={12} style={{ color: palette.text.muted, flexShrink: 0 }} />
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.stopPropagation()}
                placeholder={searchPlaceholder}
                className="w-full"
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  color: palette.text.primary,
                  fontSize: 12,
                }}
              />
            </div>
          )}
          {filteredOptions.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); setQuery(''); }}
              className="flex items-center w-full text-left px-2.5 py-2 rounded-lg text-sm transition-colors"
              style={{
                color: opt.value === value ? palette.accents.warm.base : palette.text.primary,
                backgroundColor: 'transparent',
                fontWeight: opt.value === value ? 600 : 400,
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = palette.neutrals.surfaceHover; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {opt.label}
            </button>
          ))}
          {filteredOptions.length === 0 && (
            <div className="px-2.5 py-3 text-xs" style={{ color: palette.text.muted }}>
              No models found
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function fmtCost(usd) {
  if (usd === null || usd === undefined) return null;
  return usd < 0.01 ? `$${(usd * 1000).toFixed(2)}m` : `$${usd.toFixed(4)}`;
}

function fmtMs(ms) {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
}

function deltaColor(val) {
  if (val === 0 || val === null) return palette.text.muted;
  return val < 0 ? palette.status.success : palette.status.danger;
}

function deltaPrefix(val) {
  if (val === 0) return '';
  return val < 0 ? '' : '+';
}

// ─────────────────────────────────────────────────────────────────────────────
// BusinessTaskList — first view
// ─────────────────────────────────────────────────────────────────────────────
const SIGNAL_LABELS = {
  hallucination: { label: 'Groundedness', color: palette.status.danger },
  high_cost:     { label: 'High Cost',     color: palette.accents.warm.base },
  high_latency:  { label: 'High Latency',  color: palette.status.warning },
  task_failure:  { label: 'Task Failure',  color: palette.status.danger },
};

const WORKFLOW_TABLE_COLUMNS = 'minmax(220px, 1fr) 100px 116px 82px 146px 96px';
const HISTORY_TABLE_COLUMNS = 'minmax(240px, 1fr) 118px 70px 76px 86px 80px 90px 100px 72px';
const SIM_TABLE_GAP = '14px';

const WorkflowList = ({ workflows, loading, onSelect, onNewSimulation }) => {
  const [hoveredRowId, setHoveredRowId] = useState(null);

  return (
    <div className="p-6 flex flex-col gap-4">
      <div className="mb-2">
        <h2 className="text-base font-semibold" style={{ color: palette.text.heading }}>Simulation Studio</h2>
        <p className="text-sm mt-1" style={{ color: palette.text.muted }}>
          Select a business task to simulate. Lumoz will replay it with your overrides and measure the impact.
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-8" style={{ color: palette.text.muted }}>
          <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${withOpacity(palette.accents.primary.base, 30)}`, borderTopColor: palette.accents.primary.base, animation: 'spin 0.8s linear infinite' }} />
          <span className="text-sm">Loading business tasks…</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {!loading && workflows.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-sm" style={{ color: palette.text.muted }}>No business tasks detected yet. Send some traces to get started.</p>
        </div>
      )}

      <div className="overflow-visible rounded-md border" style={{ borderColor: ds.card.borderColor, maxWidth: 1180 }}>
        <div
          className={tableGridHeaderClassName}
          style={{
            gridTemplateColumns: WORKFLOW_TABLE_COLUMNS,
            columnGap: SIM_TABLE_GAP,
          }}
        >
          {['Business Task', 'Avg Cost', 'Avg Latency', 'Traces', '', ''].map(h => (
            <span key={h}>{h}</span>
          ))}
        </div>
        {workflows.map((wf, index) => {
        const totalSignals = Object.values(wf.signal_counts || {}).reduce((a, b) => a + b, 0);
        const topSignals = Object.entries(wf.signal_counts || {}).sort((a, b) => b[1] - a[1]).slice(0, 3);
        return (
          <div
            key={wf.workflow_id}
            onClick={() => onSelect(wf)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(wf);
              }
            }}
            className={`grid items-center px-3 py-2.5 ${ds.table.row.border}`}
            style={{
              ...getTableRowStyle(index, {
                isHovered: hoveredRowId === wf.workflow_id,
                interactive: true,
                style: {
                  gridTemplateColumns: WORKFLOW_TABLE_COLUMNS,
                  columnGap: SIM_TABLE_GAP,
                },
              }),
            }}
            onMouseEnter={() => setHoveredRowId(wf.workflow_id)}
            onMouseLeave={() => setHoveredRowId(null)}
          >
            <div className="flex flex-col gap-1 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-semibold truncate" style={{ color: getTableCellColor('entity') }}>{wf.workflow_name}</span>
                {totalSignals > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: palette.severity.critical.bg, color: palette.status.danger, flexShrink: 0 }}>
                    {totalSignals} signal{totalSignals !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {topSignals.length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                  {topSignals.map(([signal, count]) => {
                    const cfg = SIGNAL_LABELS[signal];
                    return cfg ? (
                      <span key={signal} className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${cfg.color}18`, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
                        {cfg.label} ×{count}
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>
            <MetricValue icon={<DollarSign size={11} />} value={wf.avg_cost_usd > 0 ? `$${wf.avg_cost_usd.toFixed(4)}` : '—'} color={wf.avg_cost_usd > 0.03 ? palette.status.danger : getTableCellColor('metric')} />
            <MetricValue icon={<Clock size={11} />} value={wf.p50_latency_ms > 0 ? fmtMs(wf.p50_latency_ms) : '—'} color={wf.p50_latency_ms > 8000 ? palette.status.warning : getTableCellColor('metric')} />
            <span className="text-xs tabular-nums" style={{ color: getTableCellColor('metric') }}>{wf.trace_count ?? 0}</span>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onNewSimulation(wf);
              }}
              onKeyDown={e => e.stopPropagation()}
              className="flex items-center justify-center gap-1.5 text-xs font-semibold"
              style={{
                padding: '5px 10px',
                borderRadius: 6,
                border: 'none',
                backgroundColor: palette.accents.primary.base,
                color: palette.text.inverse,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
              title="Configure a new simulation"
            >
              <Play size={10} fill="currentColor" /> New simulation
            </button>
            <div className="flex items-center justify-end gap-1 text-xs font-medium" style={{ color: getTableCellColor('muted') }}>
              History ({wf.simulation_run_count ?? 0}) <ChevronRight size={13} />
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
};

const MetricValue = ({ icon, value, color }) => (
  <div className="flex items-center gap-1">
    <span style={{ color: palette.text.muted }}>{icon}</span>
    <span className="text-xs font-semibold" style={{ color }}>{value}</span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Result metric colors
// ─────────────────────────────────────────────────────────────────────────────
const REC_COLOR = {
  cost:    palette.accents.warm.base,
  latency: palette.accents.secondary.base,
  quality: palette.status.success,
  semantic: palette.accents.primary.base,
};

// Copy for each backend semantic_similarity_unavailable_reason code — 'no key
// configured' and 'the key/call actually failed' read differently to the user
// instead of both collapsing into one generic "Not evaluated" message.
// actionable reasons point admins at Settings; non-actionable ones (rate
// limits, transient provider errors) don't, since there's nothing to configure.
const SEMANTIC_UNAVAILABLE_COPY = {
  no_key: { text: 'Requires an OpenAI key', actionable: true },
  auth_error: { text: 'OpenAI key was rejected — check it’s valid', actionable: true },
  rate_limited: { text: 'OpenAI rate-limited this request', actionable: false },
  provider_error: { text: 'OpenAI embeddings request failed', actionable: false },
  unknown_error: { text: 'OpenAI embeddings request failed', actionable: false },
};

// ─────────────────────────────────────────────────────────────────────────────
// OperationConfigPanel — workflow-first agent LLM operation overrides
// ─────────────────────────────────────────────────────────────────────────────
const OperationConfigPanel = ({
  operations, loading, overrides, models, configuredProviders, onChange, onClear,
  overrideCount, runStatus, runError, onRun, sampleSize, onSampleSizeChange, estimatedReplayLlmRuns, costEstimate,
  representativeTrace, traceDetailUrl,
}) => {
  const [previewTarget, setPreviewTarget] = useState(null);
  const [bulkModelSuggestion, setBulkModelSuggestion] = useState(null);
  const modelOptions = useMemo(() => models.map(m => ({
    value: m.id,
    label: `${m.id} (${m.provider}) — $${m.cost_per_1m_in}/1M in · $${m.cost_per_1m_out}/1M out`,
  })), [models]);
  const exampleSpansByOperationKey = useMemo(() => {
    const byKey = {};
    const spans = representativeTrace?.spans || [];
    spans.forEach(span => {
      const key = span.operation?.llm_operation_model_key;
      if (key && !byKey[key]) byKey[key] = span;
    });
    return byKey;
  }, [representativeTrace]);

  useEffect(() => {
    setPreviewTarget(null);
  }, [representativeTrace?.trace_id]);
  const estimatedCostDeltaPct = costEstimate?.original_cost_usd > 0 && costEstimate?.estimated_cost_usd != null
    ? Math.round((costEstimate.estimated_cost_usd - costEstimate.original_cost_usd) / costEstimate.original_cost_usd * 100)
    : null;
  const formatCallsPerTrace = (calls = {}) => {
    const min = calls.min ?? calls.p50 ?? 0;
    const max = calls.max ?? calls.p50 ?? min;
    return min === max ? `${min}` : `${min}-${max}`;
  };

  const buildOverride = (operation, patch) => {
    const key = operation.llm_operation_model_key;
    const current = overrides[key] || {};
    const nextModel = patch.model !== undefined ? patch.model : current.model;
    const selectedModel = models.find(m => m.id === nextModel);
    const base = {
      llm_operation_id: operation.llm_operation_id,
      llm_operation_model_key: operation.llm_operation_model_key,
      agent_name: operation.agent_name,
      llm_span_name: operation.llm_span_name,
      original_provider: operation.original_provider,
      original_model: operation.original_model,
      ...current,
      ...patch,
    };
    return {
      ...base,
      provider: selectedModel?.provider || base.provider || operation.original_provider,
      model: nextModel || base.model || operation.original_model,
    };
  };

  const updateOverride = (operation, patch) => {
    const key = operation.llm_operation_model_key;
    const nextOverride = buildOverride(operation, patch);
    onChange(key, nextOverride);
    if (patch.model !== undefined && patch.model && operations.length > 1) {
      setBulkModelSuggestion({ operationKey: key, model: patch.model });
    }
  };

  const applySuggestedModelToAll = () => {
    if (!bulkModelSuggestion?.model) return;
    operations.forEach(operation => {
      onChange(operation.llm_operation_model_key, buildOverride(operation, { model: bulkModelSuggestion.model }));
    });
    setBulkModelSuggestion(null);
  };

  if (loading) {
    return (
      <div className="rounded-xl p-5 flex items-center gap-3" style={{ border: `1px solid ${palette.borders.medium}` }}>
        <div className="w-4 h-4 rounded-full border-2 animate-spin" style={{ borderColor: `${palette.accents.primary.base}30`, borderTopColor: palette.accents.primary.base }} />
        <span className="text-sm" style={{ color: palette.text.muted }}>Loading agent LLM operations…</span>
      </div>
    );
  }

  if (!operations.length) {
    return (
      <div className="rounded-xl p-5 flex items-center gap-3" style={{ border: `1px solid ${palette.borders.medium}` }}>
        <AlertTriangle size={14} style={{ color: palette.status.warning }} />
        <span className="text-sm" style={{ color: palette.text.muted }}>No LLM operations found in the selected business task sample.</span>
      </div>
    );
  }

  return (
    <>
    <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${palette.borders.medium}` }}>
      {/* No API keys warning */}
      {configuredProviders != null && configuredProviders.size === 0 && (
        <div className="flex items-center gap-2 px-4 py-2" style={{ backgroundColor: `${palette.status.warning}12`, borderBottom: `1px solid ${palette.status.warning}30` }}>
          <AlertTriangle size={12} style={{ color: palette.status.warning, flexShrink: 0 }} />
          <span className="text-xs" style={{ color: palette.text.supporting }}>
            No API keys configured — simulation will run without calling any LLMs.{' '}
            <a href="/settings" style={{ color: palette.accents.primary.base, textDecoration: 'underline' }}>Add one in Settings.</a>
          </span>
        </div>
      )}

      <div className="flex items-center px-4 py-3" style={{ borderBottom: `1px solid ${palette.borders.subtle}`, backgroundColor: palette.neutrals.surfaceMid }}>
        <span
          title="Span replay compares the same historical LLM inputs: original trace had N matching LLM runs, replay runs those same N inputs with the selected model."
          className="flex items-center gap-1.5 text-xs"
          style={{ color: palette.text.muted, flexShrink: 0 }}
        >
          <Info size={12} /> Fixed-input span replay
        </span>
        {representativeTrace?.trace_id && (
          <a
            href={traceDetailUrl(representativeTrace.trace_id)}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open representative trace ${representativeTrace.trace_id}`}
            className="flex items-center gap-1.5 text-xs"
            style={{ marginLeft: 'auto', color: palette.accents.primary.base, textDecoration: 'none', fontWeight: 600 }}
          >
            <span style={{ color: palette.text.muted, fontWeight: 500 }}>Representative trace</span>
            <span className="font-mono">{representativeTrace.trace_id.slice(0, 8)}…</span>
            <ExternalLink size={11} />
          </a>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(260px, 1.4fr) minmax(180px, 0.9fr) 70px 82px 110px minmax(220px, 1.1fr) 86px 86px 116px',
            minWidth: 1284,
            gap: 10,
            alignItems: 'center',
            padding: '7px 14px',
            backgroundColor: palette.neutrals.panelSecondary,
            borderBottom: `1px solid ${palette.borders.subtle}`,
          }}
        >
          {['Agent LLM operation', 'Current model', 'Traces', 'LLM runs', 'Per trace', 'New model', 'Temp', 'Max tokens', ''].map(h => (
            <span key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: palette.text.muted }}>
              {h}
            </span>
          ))}
        </div>

        {operations.map((operation, idx) => {
          const key = operation.llm_operation_model_key;
          const override = overrides[key] || {};
          const hasOverride = Boolean(overrides[key]);
          const showBulkSuggestion = bulkModelSuggestion?.operationKey === key && operations.length > 1;
          const calls = operation.calls_per_trace || {};
          const warnings = operation.warnings || [];
          const currentModelMissing = operation.original_model && !models.find(m => m.id === operation.original_model);
          const hasNoProviders = configuredProviders != null && configuredProviders.size === 0;
          const replacementValue = override.model || '';
          const exampleSpan = operation.example_span || exampleSpansByOperationKey[key];
          const exampleTraceId = operation.example_trace_id || representativeTrace?.trace_id;

          return (
            <React.Fragment key={key}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(260px, 1.4fr) minmax(180px, 0.9fr) 70px 82px 110px minmax(220px, 1.1fr) 86px 86px 116px',
                  minWidth: 1284,
                  gap: 10,
                  alignItems: 'center',
                  padding: '10px 14px',
                  borderTop: idx > 0 ? `1px solid ${palette.borders.subtle}` : 'none',
                  backgroundColor: hasOverride ? `${palette.accents.primary.base}08` : 'transparent',
                }}
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold truncate" style={{ color: palette.text.primary }}>
                      {operation.agent_name} / {operation.llm_span_name}
                    </span>
                    {exampleSpan && (
                      <button
                        type="button"
                        onClick={() => setPreviewTarget({ operation, span: exampleSpan, traceId: exampleTraceId })}
                        title="Inspect prompt and example input from a sampled trace"
                        aria-label="Inspect prompt and example input"
                        style={{
                          width: 24,
                          height: 24,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 5,
                          border: `1px solid ${palette.accents.primary.base}35`,
                          backgroundColor: `${palette.accents.primary.base}10`,
                          color: palette.accents.primary.base,
                          flexShrink: 0,
                        }}
                      >
                        <Search size={12} />
                      </button>
                    )}
                  </div>
                  {warnings.length > 0 && (
                    <span className="text-xs" style={{ color: palette.text.muted }}>{warnings.join(', ')}</span>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-xs font-mono" style={{ color: palette.text.supporting }}>{operation.original_model || 'unknown'}</span>
                  {currentModelMissing && (
                    <span className="text-xs" style={{ color: palette.text.muted }}>{operation.original_provider || 'unknown provider'}</span>
                  )}
                </div>

                <span className="text-sm" style={{ color: palette.text.primary }}>{operation.traces}</span>
                <span className="text-sm" style={{ color: palette.text.primary }}>{operation.llm_runs}</span>
                <span className="text-xs" style={{ color: palette.text.supporting }}>
                  {formatCallsPerTrace(calls)}
                </span>

                <StyledSelect
                  value={replacementValue}
                  placeholder={hasNoProviders ? 'No API keys configured' : 'Select model…'}
                  onChange={model => updateOverride(operation, { model })}
                  searchable
                  searchPlaceholder="Search models…"
                  options={[
                    ...(operation.original_model && !models.find(m => m.id === operation.original_model)
                      ? [{ value: operation.original_model, label: `${operation.original_model} — current` }]
                      : []),
                    ...modelOptions,
                  ]}
                />

                <input
                  type="number"
                  min="0"
                  max="2"
                  step="0.1"
                  value={override.temperature ?? ''}
                  placeholder="auto"
                  onChange={e => updateOverride(operation, { temperature: e.target.value === '' ? null : parseFloat(e.target.value) })}
                  style={{ ...ds.select, width: '100%', padding: '0.45rem 0.5rem', fontSize: 12 }}
                />

                <input
                  type="number"
                  min="1"
                  value={override.max_tokens ?? ''}
                  placeholder="auto"
                  onChange={e => updateOverride(operation, { max_tokens: e.target.value === '' ? null : parseInt(e.target.value) })}
                  style={{ ...ds.select, width: '100%', padding: '0.45rem 0.5rem', fontSize: 12 }}
                />

                <div className="flex items-center justify-end gap-2" style={{ paddingLeft: 8 }}>
                  {exampleSpan && (
                    <button
                      type="button"
                      onClick={() => setPreviewTarget({ operation, span: exampleSpan, traceId: exampleTraceId })}
                      title="Inspect prompt and example input from a sampled trace"
                      className="flex items-center gap-1"
                      style={{
                        padding: '4px 9px',
                        borderRadius: 5,
                        border: `1px solid ${palette.accents.primary.base}35`,
                        backgroundColor: `${palette.accents.primary.base}12`,
                        color: palette.accents.primary.base,
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      <Search size={11} />
                      Details
                    </button>
                  )}
                  <button
                    onClick={() => onClear(key)}
                    disabled={!hasOverride}
                    title="Clear override"
                    style={{
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 6,
                      border: `1px solid ${palette.borders.subtle}`,
                      color: hasOverride ? palette.text.muted : palette.borders.strong,
                      background: 'transparent',
                      cursor: hasOverride ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <RotateCcw size={12} />
                  </button>
                </div>
              </div>
              {showBulkSuggestion && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(260px, 1.4fr) minmax(180px, 0.9fr) 70px 82px 110px minmax(220px, 1.1fr) 86px 86px 116px',
                    minWidth: 1284,
                    gap: 10,
                    alignItems: 'center',
                    padding: '0 14px 10px',
                    borderTop: `1px solid ${palette.borders.subtle}`,
                    backgroundColor: `${palette.accents.primary.base}08`,
                  }}
                >
                  <div
                    className="flex items-center gap-2"
                    style={{
                      gridColumn: '6 / 10',
                      paddingTop: 8,
                    }}
                  >
                    <span style={{ fontSize: 12, color: palette.text.supporting, minWidth: 0 }}>
                      Apply <span style={{ color: palette.text.primary, fontFamily: 'monospace', fontWeight: 700 }}>{bulkModelSuggestion.model}</span> to all {operations.length} LLM operations?
                    </span>
                    <button
                      type="button"
                      onClick={applySuggestedModelToAll}
                      className="flex items-center gap-1"
                      style={{
                        padding: '4px 9px',
                        borderRadius: 5,
                        border: 'none',
                        backgroundColor: palette.accents.primary.base,
                        color: palette.text.inverse,
                        fontSize: 11,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      Apply all
                    </button>
                    <button
                      type="button"
                      onClick={() => setBulkModelSuggestion(null)}
                      aria-label="Dismiss apply all suggestion"
                      title="Dismiss"
                      style={{
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 5,
                        border: `1px solid ${palette.borders.subtle}`,
                        backgroundColor: 'transparent',
                        color: palette.text.muted,
                        flexShrink: 0,
                      }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Run controls — sit right after the last row, so the action follows the edits it acts on */}
      <div className="flex items-center gap-3 px-4 py-3" style={{ borderTop: `1px solid ${palette.borders.subtle}`, backgroundColor: palette.neutrals.surfaceMid }}>
        <div
          className="flex items-center gap-2"
          style={{
            padding: '7px 11px',
            borderRadius: 6,
            border: `1px solid ${estimatedCostDeltaPct != null ? withOpacity(deltaColor(estimatedCostDeltaPct), 35) : palette.borders.subtle}`,
            backgroundColor: estimatedCostDeltaPct != null ? withOpacity(deltaColor(estimatedCostDeltaPct), 10) : palette.neutrals.surfaceSubtle,
            color: palette.text.primary,
            minHeight: 34,
          }}
        >
          <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: palette.text.muted }}>
            Estimated changes
          </span>
          {overrideCount === 0 ? (
            <span style={{ fontSize: 12, fontWeight: 700, color: palette.text.muted }}>Select a model</span>
          ) : costEstimate ? (
            <>
              <span style={{ fontSize: 12, fontWeight: 700, color: estimatedCostDeltaPct != null ? deltaColor(estimatedCostDeltaPct) : palette.text.primary }}>
                Cost:{' '}
                {costEstimate.original_cost_usd != null ? `$${costEstimate.original_cost_usd.toFixed(4)}` : '—'}
                {' → '}
                {costEstimate.estimated_cost_usd != null ? `$${costEstimate.estimated_cost_usd.toFixed(4)}` : '—'}
                {estimatedCostDeltaPct != null && ` (${deltaPrefix(estimatedCostDeltaPct)}${estimatedCostDeltaPct}%)`}
              </span>
              <span style={{ fontSize: 11, color: palette.text.muted }}>{costEstimate.uncertainty || 'estimate'}</span>
            </>
          ) : (
            <span style={{ fontSize: 12, fontWeight: 700, color: palette.text.muted }}>Calculating…</span>
          )}
        </div>
        <div className="flex items-center gap-1.5" style={{ color: palette.text.muted, marginLeft: 'auto' }}>
          <span className="text-xs">Sample</span>
          <div className="flex items-center" style={{ border: `1px solid ${palette.borders.subtle}`, borderRadius: 6, overflow: 'hidden' }}>
            {SAMPLE_SIZE_OPTIONS.map(size => {
              const active = sampleSize === size;
              return (
                <button
                  key={size}
                  type="button"
                  onClick={() => onSampleSizeChange(size)}
                  disabled={runStatus === 'running'}
                  style={{
                    minWidth: 28,
                    height: 24,
                    padding: '0 7px',
                    border: 'none',
                    borderRight: size === SAMPLE_SIZE_OPTIONS[SAMPLE_SIZE_OPTIONS.length - 1] ? 'none' : `1px solid ${palette.borders.subtle}`,
                    backgroundColor: active ? palette.accents.primary.base : 'transparent',
                    color: active ? palette.text.inverse : palette.text.muted,
                    fontSize: 11,
                    fontWeight: active ? 700 : 500,
                    cursor: runStatus === 'running' ? 'not-allowed' : 'pointer',
                  }}
                >
                  {size}
                </button>
              );
            })}
          </div>
          {overrideCount > 0 && estimatedReplayLlmRuns != null && (
            <span className="text-xs" style={{ color: estimatedReplayLlmRuns > 100 ? palette.status.warning : palette.text.muted }}>
              ~{estimatedReplayLlmRuns} LLM run{estimatedReplayLlmRuns === 1 ? '' : 's'}
            </span>
          )}
        </div>

        <button
          onClick={onRun}
          disabled={overrideCount === 0 || runStatus === 'running'}
          className="flex items-center transition-all"
          style={{
            gap: 5, padding: '4px 12px', borderRadius: 6, border: 'none',
            background: overrideCount > 0 ? palette.accents.primary.base : palette.neutrals.surfaceSubtle,
            color: overrideCount > 0 ? palette.text.inverse : palette.text.muted,
            fontSize: 11, fontWeight: 600,
            cursor: overrideCount > 0 ? 'pointer' : 'not-allowed',
            opacity: runStatus === 'running' ? 0.75 : 1,
            flexShrink: 0,
          }}
        >
          {runStatus === 'running' ? (
            <><div style={{ width: 10, height: 10, borderRadius: '50%', border: `1.5px solid ${palette.borders.subtle}`, borderTopColor: palette.text.inverse, animation: 'spin 0.7s linear infinite' }} />Running…</>
          ) : (
            <><Play size={10} fill="currentColor" />Run Simulation</>
          )}
        </button>
      </div>

      {runStatus === 'error' && (
        <div className="flex items-center gap-2 px-4 py-2" style={{ borderTop: `1px solid ${palette.status.danger}30`, backgroundColor: `${palette.status.danger}10` }}>
          <AlertTriangle size={12} style={{ color: palette.status.danger, flexShrink: 0 }} />
          <span className="text-xs" style={{ color: palette.status.danger }}>{runError || 'Simulation failed'}</span>
        </div>
      )}
    </div>
    <SpanInspector
      isOpen={Boolean(previewTarget)}
      onClose={() => setPreviewTarget(null)}
      title={previewTarget ? spanDisplayName({
        ...previewTarget.span,
        agent_name: previewTarget.operation.agent_name,
        llm_span_name: previewTarget.operation.llm_span_name,
      }) : ''}
      subtitle={previewTarget?.span.llm_model_name || previewTarget?.operation.original_model || ''}
      loading={false}
      error={null}
      details={previewTarget ? spanPreviewDetails(previewTarget.span) : null}
      contextTitle="Example trace context"
      contextDescription="System prompt, inputs, and tool context from one sampled trace."
      contextAction={previewTarget?.traceId ? {
        label: `Open trace ${previewTarget.traceId.slice(0, 8)}…`,
        href: traceDetailUrl(previewTarget.traceId),
      } : null}
    />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ResultsPanelV2 — delta as hero, aggregated span breakdown, clickable history
// ─────────────────────────────────────────────────────────────────────────────

// Aggregate replayed span results by operation/model across all traces.
function aggregateSpanChanges(runResult) {
  if (runResult?.operation_results?.length) {
    return runResult.operation_results.map(result => ({
      span_name: `${result.agent_name || 'Unknown agent'} / ${result.llm_span_name || 'LLM'}`,
      llm_operation_model_key: result.llm_operation_model_key,
      agent_name: result.agent_name,
      llm_span_name: result.llm_span_name,
      original_model: result.original_model,
      replay_model: result.replay_model,
      count: result.llm_runs,
      avg_orig_cost: null,
      avg_replay_cost: null,
      avg_orig_lat: null,
      avg_replay_lat: null,
      cost_pct: null,
      lat_pct: null,
      avg_similarity: result.avg_similarity,
      similarity_percentiles: result.similarity_percentiles,
      avg_semantic_similarity: result.avg_semantic_similarity,
      semantic_similarity_percentiles: result.semantic_similarity_percentiles,
    }));
  }
  const spans = (runResult?.per_trace || []).flatMap(t => t.span_results || []).filter(s => s.replayed);
  if (!spans.length) return [];
  const avg = arr => arr.reduce((a, b) => a + b, 0) / arr.length;
  const groupMap = {};
  spans.forEach(s => {
    const displayName = spanDisplayName(s);
    const key = `${s.llm_operation_model_key || displayName}||${s.original_model}||${s.replay_model}`;
    if (!groupMap[key]) {
      groupMap[key] = {
        span_name: displayName,
        llm_operation_model_key: s.llm_operation_model_key,
        agent_name: s.agent_name,
        llm_span_name: s.llm_span_name,
        original_model: s.original_model,
        replay_model: s.replay_model,
        origCosts: [],
        replayCosts: [],
        origLats: [],
        replayLats: [],
        similarities: [],
        semanticSimilarities: [],
      };
    }
    const g = groupMap[key];
    g.origCosts.push(s.original_cost_usd);
    g.replayCosts.push(s.replay_cost_usd);
    g.origLats.push(s.original_latency_ms);
    g.replayLats.push(s.replay_latency_ms);
    if (s.similarity != null) g.similarities.push(s.similarity);
    if (s.semantic_similarity != null) g.semanticSimilarities.push(s.semantic_similarity);
  });
  return Object.values(groupMap).map(g => {
    const avgOrigCost = avg(g.origCosts), avgReplayCost = avg(g.replayCosts);
    const avgOrigLat  = avg(g.origLats),  avgReplayLat  = avg(g.replayLats);
    return {
      span_name:      g.span_name,
      llm_operation_model_key: g.llm_operation_model_key,
      agent_name: g.agent_name,
      llm_span_name: g.llm_span_name,
      original_model: g.original_model,
      replay_model:   g.replay_model,
      count:          g.origCosts.length,
      avg_orig_cost:  avgOrigCost,
      avg_replay_cost: avgReplayCost,
      avg_orig_lat:   avgOrigLat,
      avg_replay_lat: avgReplayLat,
      cost_pct:  avgOrigCost > 0 ? Math.round((avgReplayCost - avgOrigCost) / avgOrigCost * 100) : null,
      lat_pct:   avgOrigLat  > 0 ? Math.round((avgReplayLat  - avgOrigLat)  / avgOrigLat  * 100) : null,
      avg_similarity: g.similarities.length ? avg(g.similarities) : null,
      avg_semantic_similarity: g.semanticSimilarities.length ? avg(g.semanticSimilarities) : null,
    };
  });
}

const SAMPLE_SIZE_OPTIONS = [1, 3, 5, 10];

const copyToClipboard = (value) => {
  if (!value) return;
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(String(value)).catch(() => {});
  }
};

const RunIdPill = ({ runId }) => {
  if (!runId) return null;
  return (
    <button
      type="button"
      title={`Copy run id ${runId}`}
      onClick={e => {
        e.stopPropagation();
        copyToClipboard(runId);
      }}
      className="inline-flex items-center"
      style={{
        gap: 4,
        padding: '2px 6px',
        borderRadius: 5,
        border: `1px solid ${palette.borders.subtle}`,
        backgroundColor: palette.neutrals.surfaceSubtle,
        color: palette.text.muted,
        fontSize: 10,
        fontFamily: 'monospace',
        cursor: 'copy',
      }}
    >
      <span>{String(runId).slice(0, 8)}…</span>
      <Copy size={10} />
    </button>
  );
};

// Matches the "Agent / span" label used in the Agent LLM operations config panel,
// so a span reads the same way everywhere it's shown in the results.
const spanDisplayName = s => (s.agent_name ? `${s.agent_name} / ${s.llm_span_name || s.span_name}` : (s.llm_span_name || s.span_name));

const spanIdPreview = spanId => {
  if (!spanId) return '';
  const id = String(spanId);
  return id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
};

const compactText = (value, max = 140) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

const spanUserInput = span => {
  const messages = Array.isArray(span?.input_messages) ? span.input_messages : [];
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'user' && messages[i]?.content) return messages[i].content;
  }
  return span?.user_message || '';
};

const spanPreviewDetails = span => span ? ({
  input: {
    system_prompt: span.system_prompt || '',
    user_message: span.user_message || '',
    messages: span.input_messages || [],
    messages_source: span.input_messages_source,
    input_messages_truncated: Boolean(span.input_messages_truncated),
    input_messages_parse_error: Boolean(span.input_messages_parse_error),
    tool_schemas: span.tool_schemas || [],
    tool_schemas_truncated: Boolean(span.tool_schemas_truncated),
    tool_schemas_parse_error: Boolean(span.tool_schemas_parse_error),
  },
}) : null;

const ResultsPanelV2 = ({
  workflow,
  hasResults,
  runResult,
  runStatus,
  runError,
  onLoadSpanDetails,
  operations,
  operationsLoading,
  representativeTrace,
  configuredProviders,
  isAdmin,
  traceDetailUrl,
}) => {
  const before = runResult?.before || {};
  const after  = runResult?.after  || {};
  const deltas = runResult?.deltas  || {};
  const showResults = hasResults;
  const [expandedTraces, setExpandedTraces] = useState(new Set());
  const [inspectorTarget, setInspectorTarget] = useState(null); // { detailKey, traceIndex, span } | null
  const [configPreviewTarget, setConfigPreviewTarget] = useState(null);
  const [spanDetails, setSpanDetails] = useState({});
  const [spanDetailsLoading, setSpanDetailsLoading] = useState({});
  const [spanDetailsErrors, setSpanDetailsErrors] = useState({});
  useEffect(() => {
    setExpandedTraces(new Set());
    setInspectorTarget(null);
    setConfigPreviewTarget(null);
    setSpanDetails({});
    setSpanDetailsLoading({});
    setSpanDetailsErrors({});
  }, [runResult?.run_id]);
  const operationExamples = useMemo(() => {
    const byKey = {};
    (operations || []).forEach(operation => {
      const key = operation.llm_operation_model_key;
      if (key && operation.example_span) {
        byKey[key] = {
          operation,
          span: operation.example_span,
          traceId: operation.example_trace_id || representativeTrace?.trace_id,
        };
      }
    });
    (representativeTrace?.spans || []).forEach(span => {
      const key = span.operation?.llm_operation_model_key;
      if (key && !byKey[key]) {
        byKey[key] = {
          operation: {
            ...(span.operation || {}),
            agent_name: span.operation?.agent_name,
            llm_span_name: span.operation?.llm_span_name,
            original_model: span.llm_model_name,
          },
          span,
          traceId: representativeTrace.trace_id,
        };
      }
    });
    return byKey;
  }, [operations, representativeTrace]);
  const operationExampleForGroup = useCallback((group) => {
    if (group.llm_operation_model_key && operationExamples[group.llm_operation_model_key]) {
      return operationExamples[group.llm_operation_model_key];
    }
    const expectedName = group.span_name || `${group.agent_name || ''} / ${group.llm_span_name || ''}`;
    return Object.values(operationExamples).find(({ operation }) => (
      (operation.agent_name || '') === (group.agent_name || '') &&
      (operation.llm_span_name || '') === (group.llm_span_name || '') &&
      (operation.original_model || '') === (group.original_model || '')
    )) || Object.values(operationExamples).find(({ operation, span }) => (
      spanDisplayName({
        ...span,
        agent_name: operation.agent_name,
        llm_span_name: operation.llm_span_name,
      }) === expectedName
    )) || null;
  }, [operationExamples]);
  const toggleTrace = (id) => setExpandedTraces(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleSpanDetails = async (detailKey, traceIndex, span) => {
    const willOpen = inspectorTarget?.detailKey !== detailKey;
    setConfigPreviewTarget(null);
    setInspectorTarget(willOpen ? { detailKey, traceIndex, span } : null);
    if (!willOpen || spanDetails[detailKey] || spanDetailsLoading[detailKey] || !onLoadSpanDetails) return;
    setSpanDetailsLoading(prev => ({ ...prev, [detailKey]: true }));
    setSpanDetailsErrors(prev => {
      const next = { ...prev };
      delete next[detailKey];
      return next;
    });
    try {
      const details = await onLoadSpanDetails({ traceIndex, spanId: span.span_id });
      setSpanDetails(prev => ({ ...prev, [detailKey]: details }));
    } catch (err) {
      setSpanDetailsErrors(prev => ({ ...prev, [detailKey]: err.message || 'Details unavailable' }));
    } finally {
      setSpanDetailsLoading(prev => {
        const next = { ...prev };
        delete next[detailKey];
        return next;
      });
    }
  };
  const openConfigPreview = (group, example) => {
    setInspectorTarget(null);
    setConfigPreviewTarget({
      group,
      ...example,
    });
  };

  const tracesWithErrors = (runResult?.per_trace || []).filter(t => t.span_results.some(s => s.error));

  const avgSimilarity = after.avg_similarity != null ? Math.round(after.avg_similarity * 100) : null;
  const avgSemanticSimilarity = after.avg_semantic_similarity != null ? Math.round(after.avg_semantic_similarity * 100) : null;
  // Semantic similarity always calls OpenAI embeddings regardless of which provider
  // the replayed spans used — so it's unavailable whenever no OpenAI key is on file,
  // even if e.g. Anthropic is configured and replay itself works fine.
  const missingOpenAiKey = configuredProviders != null && !configuredProviders.has('openai');
  // The run itself is the authoritative source for *why* semantic similarity is
  // missing ('no_key' | 'auth_error' | 'rate_limited' | 'provider_error' |
  // 'unknown_error') — it distinguishes "never had a key" from "had one but it
  // failed" (expired/rate-limited), which the pre-run configuredProviders check
  // alone can't. Fall back to that pre-run check only for older runs that predate
  // this field.
  const semanticUnavailableReason = after.semantic_similarity_unavailable_reason || (missingOpenAiKey ? 'no_key' : null);
  // avg_semantic_similarity is computed only over spans that actually got a score —
  // a missing/rate-limited key mid-run reduces coverage without raising an error, so
  // the average alone can look confident while representing a small fraction of spans.
  const semanticSimilarityCount = after.semantic_similarity_count;
  const semanticSimilarityTotal = after.llm_runs_replayed;
  const semanticSimilarityPartialCoverage =
    semanticSimilarityCount != null && semanticSimilarityTotal != null &&
    semanticSimilarityCount > 0 && semanticSimilarityCount < semanticSimilarityTotal;
  const semanticQualityNote = semanticSimilarityPartialCoverage
    ? `Based on ${semanticSimilarityCount}/${semanticSimilarityTotal} spans — the rest didn't get a score (rate limit or key issue).`
    : 'Meaning-based (OpenAI embeddings) — catches paraphrases the text diff above misses.';
  const metrics = [
    {
      label: 'Cost',
      icon: <DollarSign size={12} />,
      color: REC_COLOR.cost,
      delta: deltas.cost_pct != null ? Math.round(deltas.cost_pct) : null,
      original: before.avg_cost_usd != null ? `$${before.avg_cost_usd.toFixed(4)}` : '—',
      replay:   after.avg_cost_usd  != null ? `$${after.avg_cost_usd.toFixed(4)}`  : '—',
      isQuality: false,
      notAvailable: false,
    },
    {
      label: 'Latency',
      icon: <Clock size={12} />,
      color: REC_COLOR.latency,
      delta: deltas.latency_pct != null ? Math.round(deltas.latency_pct) : null,
      original: before.p50_latency_ms != null ? fmtMs(before.p50_latency_ms) : '—',
      replay:   after.p50_latency_ms  != null ? fmtMs(after.p50_latency_ms)  : '—',
      isQuality: false,
      notAvailable: false,
    },
    {
      label: 'Text similarity',
      icon: <Sparkles size={12} />,
      color: REC_COLOR.quality,
      delta: null,
      replay: avgSimilarity != null ? `${avgSimilarity}%` : null,
      isQuality: true,
      notAvailable: avgSimilarity == null,
      qualityNote: 'How similar the replay output is to the original — not whether it’s more correct.',
    },
    {
      label: 'Semantic similarity',
      icon: <Sparkles size={12} />,
      color: REC_COLOR.semantic,
      delta: null,
      replay: avgSemanticSimilarity != null ? `${avgSemanticSimilarity}%` : null,
      isQuality: true,
      notAvailable: avgSemanticSimilarity == null,
      unavailableReason: avgSemanticSimilarity == null ? semanticUnavailableReason : null,
      qualityNote: semanticQualityNote,
    },
  ];

  const changeGroups = showResults ? aggregateSpanChanges(runResult) : [];
  const hasChanges = showResults && (Boolean(runResult?.variant_label) || changeGroups.length > 0);

  return (
    <>
    {/* Configuration — read-only summary of what was configured for this run, shown before its results */}
    {hasChanges && (
      <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${palette.borders.medium}` }}>
        <div className="flex items-center px-4 py-2.5" style={{ borderBottom: `1px solid ${palette.borders.subtle}`, backgroundColor: palette.neutrals.surfaceMid }}>
          <h3 className="text-sm font-semibold" style={{ color: palette.text.primary }}>Configuration</h3>
        </div>
        {runResult?.variant_label && (
          <div className="px-4 pt-3 pb-2" style={{ borderBottom: changeGroups.length > 0 ? `1px solid ${palette.borders.subtle}` : 'none' }}>
            <span style={{ fontSize: 12, color: palette.text.primary, lineHeight: 1.5 }}>{runResult.variant_label}</span>
          </div>
        )}
        {changeGroups.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(240px, 1.3fr) minmax(150px, 0.8fr) minmax(150px, 0.8fr) 80px 110px 110px 90px 90px 86px',
                minWidth: 1190,
                gap: 10,
                alignItems: 'center',
                padding: '7px 14px',
                backgroundColor: palette.neutrals.panelSecondary,
                borderBottom: `1px solid ${palette.borders.subtle}`,
              }}
            >
              {['Agent LLM operation', 'Current model', 'New model', 'LLM runs', 'Cost Δ', 'Latency Δ', 'Similarity', 'Semantic', 'Details'].map(h => (
                <span key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: palette.text.muted }}>
                  {h}
                </span>
              ))}
            </div>

            {changeGroups.map((g, i) => {
              const example = operationExampleForGroup(g);
              const previewSelected = configPreviewTarget?.span === example?.span;
              return (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(240px, 1.3fr) minmax(150px, 0.8fr) minmax(150px, 0.8fr) 80px 110px 110px 90px 90px 86px',
                    minWidth: 1190,
                    gap: 10,
                    alignItems: 'center',
                    padding: '10px 14px',
                    borderTop: i > 0 ? `1px solid ${palette.borders.subtle}` : 'none',
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold" style={{ color: palette.text.primary }}>{g.span_name}</span>
                    {g.count > 1 && (
                      <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: palette.neutrals.surfaceSubtle, color: palette.text.muted, border: `1px solid ${palette.borders.subtle}` }}>
                        ×{g.count}
                      </span>
                    )}
                  </div>

                  <span className="text-xs font-mono" style={{ color: palette.text.supporting }}>{g.original_model || '—'}</span>

                  <span className="text-xs font-mono" style={{ color: g.replay_model && g.replay_model !== g.original_model ? palette.accents.primary.base : palette.text.supporting, fontWeight: g.replay_model && g.replay_model !== g.original_model ? 600 : 400 }}>
                    {g.replay_model || '—'}
                  </span>

                  <span className="text-sm" style={{ color: palette.text.primary }}>{g.count}</span>

                  {g.cost_pct != null ? (
                    <span
                      title={`$${g.avg_orig_cost.toFixed(5)} → $${g.avg_replay_cost.toFixed(5)}`}
                      className="text-xs px-1.5 py-0.5 rounded font-semibold"
                      style={{ width: 'fit-content', backgroundColor: g.cost_pct < 0 ? `${palette.status.success}15` : `${palette.status.danger}15`, color: g.cost_pct < 0 ? palette.status.success : palette.status.danger }}
                    >
                      {g.cost_pct > 0 ? '+' : ''}{g.cost_pct}%
                    </span>
                  ) : <span style={{ fontSize: 12, color: palette.text.muted }}>—</span>}

                  {g.lat_pct != null ? (
                    <span
                      title={`${fmtMs(g.avg_orig_lat)} → ${fmtMs(g.avg_replay_lat)}`}
                      className="text-xs px-1.5 py-0.5 rounded font-semibold"
                      style={{ width: 'fit-content', backgroundColor: g.lat_pct < 0 ? `${palette.status.success}15` : `${palette.status.danger}15`, color: g.lat_pct < 0 ? palette.status.success : palette.status.danger }}
                    >
                      {g.lat_pct > 0 ? '+' : ''}{g.lat_pct}%
                    </span>
                  ) : <span style={{ fontSize: 12, color: palette.text.muted }}>—</span>}

                  <span className="text-xs" style={{ color: g.avg_similarity != null ? palette.text.primary : palette.text.muted }}>
                    {g.avg_similarity != null ? `${Math.round(g.avg_similarity * 100)}%` : '—'}
                  </span>

                  <span className="text-xs" style={{ color: g.avg_semantic_similarity != null ? palette.text.primary : palette.text.muted }}>
                    {g.avg_semantic_similarity != null ? `${Math.round(g.avg_semantic_similarity * 100)}%` : '—'}
                  </span>

                  <div className="flex items-center justify-end">
                    {example?.span ? (
                      <button
                        type="button"
                        onClick={() => openConfigPreview(g, example)}
                        title="Inspect typical prompt and input for this operation"
                        className="flex items-center gap-1"
                        style={{
                          padding: '4px 8px',
                          borderRadius: 5,
                          border: `1px solid ${previewSelected ? palette.accents.primary.base : `${palette.accents.primary.base}35`}`,
                          backgroundColor: previewSelected ? `${palette.accents.primary.base}12` : 'transparent',
                          color: palette.accents.primary.base,
                          fontSize: 10,
                          fontWeight: 700,
                        }}
                      >
                        <Search size={11} />
                        Details
                      </button>
                    ) : (
                      <span style={{ fontSize: 12, color: palette.text.muted }}>
                        {operationsLoading ? '…' : '—'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    )}

    <div className="rounded-xl overflow-hidden flex-1" style={{ border: `1px solid ${palette.borders.medium}` }}>
      {/* Header */}
      <div className="flex flex-col gap-1.5 px-4 py-2.5" style={{ borderBottom: `1px solid ${palette.borders.subtle}`, backgroundColor: showResults ? palette.severity.success.bg : palette.neutrals.surfaceMid }}>
        <div className="flex items-center" style={{ gap: 10 }}>
          <h3 className="text-sm font-semibold" style={{ color: palette.text.primary }}>Simulation Results</h3>
          {showResults && runResult && (
            <div className="flex items-center" style={{ marginLeft: 'auto', gap: 8, minWidth: 0 }}>
              <RunIdPill runId={runResult.run_id} />
              <span style={{ fontSize: 11, color: palette.text.muted, whiteSpace: 'nowrap' }}>
                {runResult.confidence} · {runResult.traces_used} traces · {runResult.llm_runs_replayed || 0} LLM runs
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Replay errors — aggregate warning, so this doesn't require expanding every trace to find */}
      {tracesWithErrors.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2" style={{ backgroundColor: `${palette.status.danger}10`, borderBottom: `1px solid ${palette.status.danger}30` }}>
          <AlertTriangle size={12} style={{ color: palette.status.danger, flexShrink: 0 }} />
          <span className="text-xs" style={{ color: palette.text.supporting }}>
            {tracesWithErrors.length} of {runResult?.per_trace?.length || 0} trace{tracesWithErrors.length === 1 ? '' : 's'} had replay errors — expand a trace marked in red below to see details.
          </span>
        </div>
      )}

      {/* Empty state */}
      {!showResults && (
        <div className="flex flex-col gap-0">
          <div className="grid grid-cols-4" style={{ borderBottom: `1px solid ${palette.borders.subtle}` }}>
            {['Cost', 'Latency', 'Text similarity', 'Semantic similarity'].map((label, i) => (
              <div key={label} className="flex flex-col items-center justify-center gap-1 py-8"
                style={{ borderRight: i < 3 ? `1px solid ${palette.borders.subtle}` : 'none' }}>
                <div style={{ width: 40, height: 10, borderRadius: 5, backgroundColor: palette.borders.subtle }} />
                <div style={{ width: 28, height: 6, borderRadius: 4, backgroundColor: palette.borders.subtle, opacity: 0.5 }} />
              </div>
            ))}
          </div>
          {(runStatus === 'running' || runStatus === 'error') && (
            <div className="flex flex-col items-center gap-1 py-4">
              {runStatus === 'running' ? (
                <><div className="w-5 h-5 rounded-full border-2 animate-spin" style={{ borderColor: `${palette.accents.primary.base}30`, borderTopColor: palette.accents.primary.base }} /><p className="text-xs" style={{ color: palette.text.muted }}>Running simulation…</p></>
              ) : (
                <p className="text-xs" style={{ color: palette.status.danger }}>{runError || 'Simulation failed'}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {showResults && (
        <div className="flex flex-col">
          {/* Impact */}
          <div className="px-4 pt-3 pb-1">
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: palette.text.muted }}>Impact</span>
          </div>

          {/* 4 metric columns */}
          <div className="grid grid-cols-4" style={{ borderBottom: `1px solid ${palette.borders.subtle}` }}>
            {metrics.map((m, i) => {
              const improved = m.isQuality ? m.delta > 0 : m.delta < 0;
              const deltaColor = m.notAvailable || m.delta == null ? palette.text.muted : m.delta === 0 ? palette.text.muted : improved ? palette.status.success : palette.status.danger;
              return (
                <div key={m.label} className="flex flex-col items-center justify-center gap-1 py-6"
                  style={{ borderRight: i < metrics.length - 1 ? `1px solid ${palette.borders.subtle}` : 'none', background: m.notAvailable ? 'transparent' : `${deltaColor}05` }}>
                  <div className="flex items-center gap-1.5">
                    <span style={{ color: m.color, display: 'flex' }}>{m.icon}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: palette.text.muted }}>{m.label}</span>
                  </div>
                  {m.notAvailable ? (
                    <div className="flex flex-col items-center gap-0.5" style={{ maxWidth: 180, textAlign: 'center' }}>
                      <span style={{ fontSize: 22, fontWeight: 700, color: palette.text.muted, lineHeight: 1 }}>—</span>
                      {m.unavailableReason && SEMANTIC_UNAVAILABLE_COPY[m.unavailableReason] ? (
                        <>
                          <span style={{ fontSize: 10, color: palette.text.muted }}>{SEMANTIC_UNAVAILABLE_COPY[m.unavailableReason].text}</span>
                          {SEMANTIC_UNAVAILABLE_COPY[m.unavailableReason].actionable && (
                            isAdmin ? (
                              <a
                                href="/settings?tab=providerKeys"
                                style={{ fontSize: 9, color: palette.accents.primary.base, textDecoration: 'underline' }}
                              >
                                Fix it in Settings
                              </a>
                            ) : (
                              <span style={{ fontSize: 9, color: palette.text.muted, opacity: 0.7 }}>Ask a workspace admin to fix it</span>
                            )
                          )}
                        </>
                      ) : (
                        <>
                          <span style={{ fontSize: 10, color: palette.text.muted, textAlign: 'center' }}>Not evaluated</span>
                          <span style={{ fontSize: 9, color: palette.text.muted, opacity: 0.7 }}>Fast Replay mode</span>
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1" style={{ color: deltaColor }}>
                        {m.isQuality && m.replay ? <Sparkles size={16} /> : m.delta == null ? <Minus size={16} /> : m.delta < 0 ? <TrendingDown size={16} /> : m.delta > 0 ? <TrendingUp size={16} /> : <Minus size={16} />}
                        <span style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em' }}>
                          {m.isQuality && m.replay ? m.replay : m.delta != null && m.delta !== 0 ? `${deltaPrefix(m.delta)}${m.delta}%` : '—'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5" style={{ fontSize: 11 }}>
                        {m.isQuality ? (
                          <span style={{ color: palette.text.muted }}>{m.qualityNote}</span>
                        ) : (
                          <>
                            <span style={{ color: palette.text.muted, textDecoration: 'line-through' }}>{m.original || '—'}</span>
                            <span style={{ color: palette.text.supporting }}>→</span>
                            <span style={{ color: deltaColor, fontWeight: 600 }}>{m.replay || '—'}</span>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Per-trace breakdown */}
          {(() => {
            const perTrace = runResult?.per_trace;
            if (!perTrace?.length) return null;

            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Section label */}
                <div style={{ padding: '12px 16px 4px 16px' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: palette.text.muted
                  }}>
                    Traces ({perTrace.length})
                  </span>
                </div>

                {/* Column headers */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '140px minmax(220px, 1fr) 175px 165px 70px 90px 90px minmax(150px, 0.8fr) 24px',
                  padding: '4px 16px',
                  borderTop: `1px solid ${palette.borders.subtle}`,
                  borderBottom: `1px solid ${palette.borders.subtle}`,
                  backgroundColor: palette.neutrals.panelSecondary
                }}>
                  {['Trace', 'User input', 'Cost', 'Latency', 'Similarity', 'Semantic', 'LLM runs', 'Errors', ''].map((h, i) => (
                    <span key={i} style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
                      textTransform: 'uppercase', color: palette.text.muted
                    }}>{h}</span>
                  ))}
                </div>

                {/* Trace rows */}
                {perTrace.map((t, idx) => {
                  const costPct = t.original_cost_usd > 0
                    ? Math.round((t.replay_cost_usd - t.original_cost_usd) / t.original_cost_usd * 100)
                    : null;
                  const latPct = t.original_latency_ms > 0
                    ? Math.round((t.replay_latency_ms - t.original_latency_ms) / t.original_latency_ms * 100)
                    : null;
                  const replayedCount = t.span_results.filter(s => s.replayed).length;
                  const similarities = t.span_results.filter(s => s.replayed && s.similarity != null).map(s => s.similarity);
                  const avgTraceSimilarity = similarities.length ? similarities.reduce((a, b) => a + b, 0) / similarities.length : null;
                  const semanticSimilarities = t.span_results.filter(s => s.replayed && s.semantic_similarity != null).map(s => s.semantic_similarity);
                  const avgTraceSemanticSimilarity = semanticSimilarities.length ? semanticSimilarities.reduce((a, b) => a + b, 0) / semanticSimilarities.length : null;
                  const totalSpans = t.span_results.length;
                  const isExpanded = expandedTraces.has(t.trace_id);
                  const traceErrors = t.span_results.filter(s => s.error);

                  // Border driven by cost delta, but a replay error takes priority over the cost signal
                  const borderColor = traceErrors.length > 0 ? palette.status.danger
                    : costPct == null ? 'transparent'
                    : costPct < 0 ? palette.status.success
                    : costPct > 0 ? palette.status.danger
                    : 'transparent';

                  return (
                    <div key={t.trace_id}>
                      {/* Collapsed row */}
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '140px minmax(220px, 1fr) 175px 165px 70px 90px 90px minmax(150px, 0.8fr) 24px',
                          padding: '8px 16px',
                          alignItems: 'center',
                          borderTop: idx > 0 ? `1px solid ${palette.borders.subtle}` : 'none',
                          borderLeft: `3px solid ${borderColor}`,
                          cursor: 'pointer',
                        }}
                        onClick={() => toggleTrace(t.trace_id)}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = palette.neutrals.overlayHover; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        {/* Trace ID truncated */}
                        <a
                          href={traceDetailUrl(t.trace_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Open trace ${t.trace_id}`}
                          onClick={e => e.stopPropagation()}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            minWidth: 0,
                            width: 'fit-content',
                            fontSize: 12,
                            fontFamily: 'monospace',
                            color: palette.accents.primary.base,
                            textDecoration: 'none',
                            fontWeight: 600,
                          }}
                        >
                          <span>{t.trace_id.slice(0, 8)}…</span>
                          <ExternalLink size={11} strokeWidth={2} />
                        </a>

                        <span
                          title={t.user_input || ''}
                          style={{
                            fontSize: 11,
                            color: t.user_input ? palette.text.supporting : palette.text.muted,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          }}
                        >
                          {compactText(t.user_input, 180) || '—'}
                        </span>

                        {/* Cost before → after + delta */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, color: palette.text.muted, textDecoration: 'line-through' }}>
                            ${t.original_cost_usd.toFixed(4)}
                          </span>
                          <span style={{ fontSize: 11, color: palette.text.supporting }}>→</span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: costPct != null ? deltaColor(costPct) : palette.text.primary }}>
                            ${t.replay_cost_usd.toFixed(4)}
                          </span>
                          {costPct != null && (
                            <span style={{
                              fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                              backgroundColor: `${deltaColor(costPct)}22`,
                              color: deltaColor(costPct)
                            }}>
                              {deltaPrefix(costPct)}{costPct}%
                            </span>
                          )}
                        </div>

                        {/* Latency before → after + delta */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, color: palette.text.muted, textDecoration: 'line-through' }}>
                            {fmtMs(t.original_latency_ms)}
                          </span>
                          <span style={{ fontSize: 11, color: palette.text.supporting }}>→</span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: latPct != null ? deltaColor(latPct) : palette.text.primary }}>
                            {fmtMs(t.replay_latency_ms)}
                          </span>
                          {latPct != null && (
                            <span style={{
                              fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                              backgroundColor: `${deltaColor(latPct)}22`,
                              color: deltaColor(latPct)
                            }}>
                              {deltaPrefix(latPct)}{latPct}%
                            </span>
                          )}
                        </div>

                        <span style={{ fontSize: 11, color: avgTraceSimilarity != null ? palette.text.primary : palette.text.muted }}>
                          {avgTraceSimilarity != null ? `${Math.round(avgTraceSimilarity * 100)}%` : '—'}
                        </span>

                        <span style={{ fontSize: 11, color: avgTraceSemanticSimilarity != null ? palette.text.primary : palette.text.muted }}>
                          {avgTraceSemanticSimilarity != null ? `${Math.round(avgTraceSemanticSimilarity * 100)}%` : '—'}
                        </span>

                        {/* Replayed count */}
                        <span style={{ fontSize: 11, color: replayedCount > 0 ? palette.text.primary : palette.text.muted }}>
                          {replayedCount} / {totalSpans}
                        </span>

                        {/* Errors — first error text, or a count summary when there's more than one */}
                        {traceErrors.length > 0 ? (
                          <div
                            className="flex items-center gap-1"
                            style={{ minWidth: 0 }}
                            title={traceErrors.map(s => `${s.span_name ? spanDisplayName(s) : 'Span'}: ${s.error}`).join('\n')}
                          >
                            <AlertTriangle size={11} style={{ color: palette.status.danger, flexShrink: 0 }} />
                            <span style={{ fontSize: 11, color: palette.status.danger, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {traceErrors.length === 1 ? traceErrors[0].error : `${traceErrors.length} errors`}
                            </span>
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: palette.text.muted }}>—</span>
                        )}

                        {/* Chevron */}
                        <ChevronRight
                          size={13}
                          style={{
                            color: palette.text.muted,
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 150ms',
                          }}
                        />
                      </div>

                      {/* Expanded span sub-table */}
                      {isExpanded && (
                        <div style={{ backgroundColor: palette.neutrals.panelSecondary, borderTop: `1px solid ${palette.borders.subtle}` }}>
                          {traceErrors.length > 0 && (
                            <div
                              style={{
                                margin: '10px 16px',
                                padding: '10px 12px',
                                borderRadius: 6,
                                border: `1px solid ${palette.status.danger}40`,
                                backgroundColor: `${palette.status.danger}10`,
                                color: palette.text.primary,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <AlertTriangle size={14} style={{ color: palette.status.danger, flexShrink: 0 }} />
                                <span style={{ fontSize: 12, fontWeight: 700, color: palette.status.danger }}>
                                  Replay errors
                                </span>
                              </div>
                              <div style={{ display: 'grid', gap: 4 }}>
                                {traceErrors.map((s, errorIndex) => (
                                  <div
                                    key={`${s.span_id || s.span_name || errorIndex}:error`}
                                    style={{
                                      fontSize: 12,
                                      lineHeight: 1.45,
                                      color: palette.text.supporting,
                                      overflowWrap: 'anywhere',
                                    }}
                                  >
                                    <span style={{ fontWeight: 700, color: palette.text.primary }}>
                                      {s.span_name ? spanDisplayName(s) : `Span ${errorIndex + 1}`}:
                                    </span>{' '}
                                    {s.error}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Span column headers */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 80px 1fr 1fr 1fr 80px 80px 64px',
                            padding: '4px 24px',
                            borderBottom: `1px solid ${palette.borders.subtle}`,
                          }}>
                            {['Span', 'Status', 'Model', 'Cost', 'Latency', 'Similarity', 'Semantic', 'Details'].map((h, i) => (
                              <span key={i} style={{
                                fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
                                textTransform: 'uppercase', color: palette.text.muted
                              }}>{h}</span>
                            ))}
                          </div>

                          {/* Span rows */}
                          {t.span_results.map((s, si) => {
                            const spanCostPct = s.original_cost_usd > 0
                              ? Math.round((s.replay_cost_usd - s.original_cost_usd) / s.original_cost_usd * 100)
                              : null;
                            const spanLatPct = s.original_latency_ms > 0
                              ? Math.round((s.replay_latency_ms - s.original_latency_ms) / s.original_latency_ms * 100)
                              : null;
                            const detailKey = `${t.trace_id}:${s.span_id || si}`;
                            const traceIndex = t.trace_index ?? idx;
                            const showSpanDetails = inspectorTarget?.detailKey === detailKey;
                            const canExpandDetails = Boolean(s.replayed);

                            return (
                              <React.Fragment key={detailKey}>
                                <div
                                  onClick={() => { if (canExpandDetails) toggleSpanDetails(detailKey, traceIndex, s); }}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 80px 1fr 1fr 1fr 80px 80px 64px',
                                    padding: '6px 24px',
                                    alignItems: 'center',
                                    borderTop: si > 0 ? `1px solid ${palette.borders.subtle}` : 'none',
                                    opacity: s.replayed ? 1 : 0.6,
                                    cursor: canExpandDetails ? 'pointer' : 'default',
                                  }}
                                  onMouseEnter={e => { if (canExpandDetails) e.currentTarget.style.backgroundColor = palette.neutrals.overlayHover; }}
                                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                >
                                  {/* Span name */}
                                  <div className="min-w-0 flex flex-col gap-0.5">
                                    <span
                                      title={spanDisplayName(s)}
                                      style={{
                                        fontSize: 12,
                                        color: palette.text.primary,
                                        fontWeight: 500,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {spanDisplayName(s)}
                                    </span>
                                    {s.span_id && (
                                      <span
                                        title={s.span_id}
                                        style={{
                                          fontSize: 10,
                                          color: palette.text.muted,
                                          fontFamily: 'monospace',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {spanIdPreview(s.span_id)}
                                      </span>
                                    )}
                                  </div>

                                  {/* Replayed/Unchanged badge */}
                                  <span style={{
                                    fontSize: 10, fontWeight: 600,
                                    display: 'inline-block', width: 'fit-content',
                                    padding: '1px 6px', borderRadius: 3,
                                    backgroundColor: s.replayed
                                      ? `${palette.accents.primary.base}18`
                                      : palette.neutrals.panelSecondary,
                                    color: s.replayed ? palette.accents.primary.base : palette.text.muted,
                                    border: `1px solid ${s.replayed ? `${palette.accents.primary.base}30` : palette.borders.subtle}`,
                                  }}>
                                    {s.replayed ? 'Replayed' : 'Unchanged'}
                                  </span>

                                  {/* Model */}
                                  <div style={{ fontSize: 11 }}>
                                    {s.replayed && s.original_model !== s.replay_model ? (
                                      <>
                                        <span style={{ color: palette.text.muted, textDecoration: 'line-through', marginRight: 4 }}>
                                          {s.original_model}
                                        </span>
                                        <span style={{ color: palette.text.supporting }}>→ </span>
                                        <span style={{ color: palette.accents.primary.base, fontWeight: 600 }}>
                                          {s.replay_model}
                                        </span>
                                      </>
                                    ) : (
                                      <span style={{ color: palette.text.muted }}>{s.original_model}</span>
                                    )}
                                  </div>

                                  {/* Cost delta */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 11, color: palette.text.muted, textDecoration: 'line-through' }}>
                                      ${s.original_cost_usd.toFixed(5)}
                                    </span>
                                    <span style={{ fontSize: 11, color: palette.text.supporting }}>→</span>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: spanCostPct != null ? deltaColor(spanCostPct) : palette.text.primary }}>
                                      ${s.replay_cost_usd.toFixed(5)}
                                    </span>
                                    {spanCostPct != null && (
                                      <span style={{
                                        fontSize: 10, fontWeight: 600, padding: '1px 4px', borderRadius: 3,
                                        backgroundColor: `${deltaColor(spanCostPct)}22`,
                                        color: deltaColor(spanCostPct)
                                      }}>
                                        {deltaPrefix(spanCostPct)}{spanCostPct}%
                                      </span>
                                    )}
                                  </div>

                                  {/* Latency delta */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 11, color: palette.text.muted, textDecoration: 'line-through' }}>
                                      {fmtMs(s.original_latency_ms)}
                                    </span>
                                    <span style={{ fontSize: 11, color: palette.text.supporting }}>→</span>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: spanLatPct != null ? deltaColor(spanLatPct) : palette.text.primary }}>
                                      {fmtMs(s.replay_latency_ms)}
                                    </span>
                                    {spanLatPct != null && (
                                      <span style={{
                                        fontSize: 10, fontWeight: 600, padding: '1px 4px', borderRadius: 3,
                                        backgroundColor: `${deltaColor(spanLatPct)}22`,
                                        color: deltaColor(spanLatPct)
                                      }}>
                                        {deltaPrefix(spanLatPct)}{spanLatPct}%
                                      </span>
                                    )}
                                  </div>

                                  <span style={{ fontSize: 11, color: s.similarity != null ? palette.text.primary : palette.text.muted }}>
                                    {s.similarity != null ? `${Math.round(s.similarity * 100)}%` : '—'}
                                  </span>

                                  <span style={{ fontSize: 11, color: s.semantic_similarity != null ? palette.text.primary : palette.text.muted }}>
                                    {s.semantic_similarity != null ? `${Math.round(s.semantic_similarity * 100)}%` : '—'}
                                  </span>

                                  <div className="flex items-center justify-end gap-1">
                                    {s.error != null && (
                                      <span title={s.error}>
                                        <AlertTriangle
                                          size={13}
                                          style={{ color: palette.status.danger, cursor: 'default' }}
                                        />
                                      </span>
                                    )}
                                    {canExpandDetails && (
                                      <button
                                        type="button"
                                        onClick={e => {
                                          e.stopPropagation();
                                          toggleSpanDetails(detailKey, traceIndex, s);
                                        }}
                                        style={{
                                          padding: '2px 7px',
                                          borderRadius: 5,
                                          border: `1px solid ${showSpanDetails ? palette.accents.primary.base : palette.borders.subtle}`,
                                          backgroundColor: showSpanDetails ? `${palette.accents.primary.base}12` : 'transparent',
                                          color: showSpanDetails ? palette.accents.primary.base : palette.text.muted,
                                          fontSize: 10,
                                          fontWeight: 700,
                                        }}
                                      >
                                        {showSpanDetails ? 'Hide' : 'Details'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

        </div>
      )}
    </div>

    <SpanInspector
      isOpen={Boolean(configPreviewTarget)}
      onClose={() => setConfigPreviewTarget(null)}
      title={configPreviewTarget ? spanDisplayName({
        ...configPreviewTarget.span,
        agent_name: configPreviewTarget.operation.agent_name || configPreviewTarget.group.agent_name,
        llm_span_name: configPreviewTarget.operation.llm_span_name || configPreviewTarget.group.llm_span_name,
      }) : ''}
      subtitle={configPreviewTarget?.span.llm_model_name || configPreviewTarget?.operation.original_model || configPreviewTarget?.group.original_model || ''}
      loading={false}
      error={null}
      details={configPreviewTarget ? spanPreviewDetails(configPreviewTarget.span) : null}
      contextTitle="Example trace context"
      contextDescription="System prompt, inputs, and tool context from one sampled trace."
      contextAction={configPreviewTarget?.traceId ? {
        label: `Open trace ${configPreviewTarget.traceId.slice(0, 8)}…`,
        href: traceDetailUrl(configPreviewTarget.traceId),
      } : null}
    />
    <SpanInspector
      isOpen={Boolean(inspectorTarget)}
      onClose={() => setInspectorTarget(null)}
      title={inspectorTarget ? spanDisplayName(inspectorTarget.span) : ''}
      subtitle={
        inspectorTarget?.span.original_model && inspectorTarget?.span.replay_model && inspectorTarget.span.original_model !== inspectorTarget.span.replay_model
          ? `${inspectorTarget.span.span_id || 'unknown span'} · ${inspectorTarget.span.original_model} → ${inspectorTarget.span.replay_model}`
          : [inspectorTarget?.span.span_id, inspectorTarget?.span.original_model].filter(Boolean).join(' · ')
      }
      loading={inspectorTarget ? Boolean(spanDetailsLoading[inspectorTarget.detailKey]) : false}
      error={inspectorTarget ? spanDetailsErrors[inspectorTarget.detailKey] : null}
      details={inspectorTarget ? spanDetails[inspectorTarget.detailKey] : null}
    />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RunHistoryPanel — full-view past runs table
// ─────────────────────────────────────────────────────────────────────────────
const RunHistoryPanel = ({ runs, loadingRunId, onOpenRun, error }) => {
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const fmtDate = ts => {
    if (!ts) return '—';
    try {
      const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' +
             d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    } catch { return '—'; }
  };
  const pct = (v, invert = false) => {
    if (v == null) return <span style={{ color: palette.text.muted }}>—</span>;
    const improved = invert ? v > 0 : v < 0;
    const color = v === 0 ? palette.text.muted : improved ? palette.status.success : palette.status.danger;
    return <span style={{ color, fontWeight: 600 }}>{v > 0 ? '+' : ''}{Math.round(v)}%</span>;
  };
  const semanticPct = run => {
    const after = run.after_metrics || {};
    const value = after.avg_semantic_similarity;
    const count = after.semantic_similarity_count;
    const total = after.llm_runs_replayed;
    const reason = SEMANTIC_UNAVAILABLE_COPY[after.semantic_similarity_unavailable_reason]?.text;
    if (value == null) {
      return <span title={reason || 'Semantic similarity was not evaluated'} style={{ color: palette.text.muted }}>—</span>;
    }
    const coverage = count != null && total != null
      ? `Semantic similarity across ${count}/${total} LLM runs`
      : 'Semantic similarity';
    return <span title={coverage} style={{ color: getTableCellColor('metric'), fontWeight: 600 }}>{Math.round(value * 100)}%</span>;
  };
  const simulationLabel = run => {
    const overrides = Array.isArray(run.overrides) ? run.overrides : [];
    const changes = overrides
      .map(override => ({
        from: override.original_model || override.originalModel || '',
        to: override.model || override.new_model || '',
      }))
      .filter(change => change.from && change.to && change.from !== change.to);
    if (changes.length > 0) {
      return {
        primary: `${changes[0].from} → ${changes[0].to}`,
        more: changes.length - 1,
      };
    }

    const parts = String(run.variant_label || '').split('|').map(part => part.trim()).filter(Boolean);
    if (parts.length > 0) {
      const first = parts[0].includes(':') ? parts[0].split(':').slice(1).join(':').trim() : parts[0];
      return {
        primary: first || parts[0],
        more: Math.max(0, parts.length - 1),
      };
    }
    return { primary: '—', more: 0 };
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-semibold" style={{ color: palette.text.primary }}>Simulation History</h3>
        <p className="text-xs mt-1" style={{ color: palette.text.muted }}>
          Past simulations for this business task. Open one to see its full before/after comparison.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: `${palette.status.danger}10`, border: `1px solid ${palette.status.danger}30` }}>
          <AlertTriangle size={13} style={{ color: palette.status.danger, flexShrink: 0 }} />
          <span className="text-xs" style={{ color: palette.status.danger }}>{error}</span>
        </div>
      )}

      {!runs.length ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16" style={{ border: `1px solid ${palette.borders.medium}`, borderRadius: 12 }}>
          <Clock size={20} style={{ color: palette.text.muted }} />
          <p className="text-sm" style={{ color: palette.text.muted }}>No runs yet for this business task</p>
          <p className="text-xs" style={{ color: palette.text.muted }}>Run a simulation to see results here</p>
        </div>
      ) : (
        <div className="overflow-visible rounded-md border" style={{ borderColor: ds.card.borderColor, maxWidth: 1180 }}>
          {/* Table header */}
          <div
            className={tableGridHeaderClassName}
            style={{
              gridTemplateColumns: HISTORY_TABLE_COLUMNS,
              columnGap: SIM_TABLE_GAP,
            }}
          >
            {['Simulation', 'Run ID', 'Traces', 'Errors', 'Similarity', 'Cost Δ', 'Latency Δ', 'Date', ''].map((h, idx) => (
              <span key={h || idx} className={idx >= 2 && idx <= 6 ? 'text-right pr-2' : idx === 8 ? 'text-right pr-2' : ''}>{h}</span>
            ))}
          </div>
          {/* Rows */}
          {runs.map((run, i) => {
            const errorCount = run.error_count ?? run.after_metrics?.error_count ?? 0;
            const failed = run.status === 'failed' || Boolean(run.error_message);
            const label = simulationLabel(run);
            return (
              <div
                key={run.run_id}
                className={`grid items-center px-3 py-2.5 ${ds.table.row.border}`}
                onClick={() => onOpenRun(run)}
                style={{
                  ...getTableRowStyle(i, {
                    isHovered: hoveredRowId === run.run_id,
                    interactive: true,
                    style: {
                      gridTemplateColumns: HISTORY_TABLE_COLUMNS,
                      columnGap: SIM_TABLE_GAP,
                    },
                  }),
                }}
                onMouseEnter={() => setHoveredRowId(run.run_id)}
                onMouseLeave={() => setHoveredRowId(null)}
              >
                <div className="min-w-0">
                  <span
                    className="text-xs font-medium truncate"
                    title={run.variant_label || label.primary}
                    style={{ color: getTableCellColor('entity'), display: 'block' }}
                  >
                    {label.primary}{label.more > 0 ? ` +${label.more} more` : ''}
                  </span>
                </div>
                <div>
                  <RunIdPill runId={run.run_id} />
                </div>
                <div className="text-right pr-2">
                  <span className="text-xs tabular-nums" style={{ color: getTableCellColor('metric') }}>{run.traces_used ?? '—'}</span>
                </div>
                <div className="text-right pr-2">
                  {failed || errorCount > 0 ? (
                    <span
                      className="inline-flex items-center gap-1 text-xs font-semibold"
                      title={run.error_message || `${errorCount} replay error${errorCount === 1 ? '' : 's'}`}
                      style={{ color: palette.status.danger }}
                    >
                      <AlertTriangle size={11} />{failed ? 'Failed' : errorCount}
                    </span>
                  ) : (
                    <span className="text-xs" style={{ color: palette.text.muted }}>—</span>
                  )}
                </div>
                <div className="text-right pr-2">
                  <span className="text-xs">{semanticPct(run)}</span>
                </div>
                <div className="text-xs text-right pr-2">{pct(run.cost_delta_pct)}</div>
                <div className="text-xs text-right pr-2">{pct(run.latency_delta_pct)}</div>
                <div>
                  <span className="text-xs" style={{ color: getTableCellColor('time') }}>{fmtDate(run.created_at)}</span>
                </div>
                <div className="flex justify-end pr-2">
                  <span className="text-xs font-semibold" style={{ color: palette.accents.primary.base }}>
                    {loadingRunId === run.run_id ? 'Loading' : 'Open'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main SimulationStudio
// ─────────────────────────────────────────────────────────────────────────────
const SimulationStudio = ({ appId, environment }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Auth ──────────────────────────────────────────────────────────────────
  const { getJwt } = useSessionJwt();
  // Derived fresh each render from live Stytch member state — cheap, so no
  // memoization/dependency array is needed (avoids keying anything on the
  // `member` object, which — like `session` — gets a new identity on Stytch's
  // heartbeat; see Auth Consumption Pattern in console/CLAUDE.md).
  const { member } = useStytchMember();
  const isAdmin = hasAdminRole(member?.roles);
  const { selectedRange } = useTimeRange();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  const workflowReplayUrl = useCallback((path, extraParams) => {
    const params = new URLSearchParams();
    if (environment) params.set('env', environment);
    if (extraParams) Object.entries(extraParams).forEach(([k, v]) => params.set(k, v));
    const query = params.toString();
    return `${API_URL}${v2ServicePath(appId, path)}${query ? `?${query}` : ''}`;
  }, [API_URL, appId, environment]);
  const traceDetailUrl = useCallback((traceId) => {
    const params = new URLSearchParams();
    if (environment) params.set('env', environment);
    const query = params.toString();
    return `/app/${encodeURIComponent(appId)}/traces/${encodeURIComponent(traceId)}${query ? `?${query}` : ''}`;
  }, [appId, environment]);
  const unwrapV2 = (payload) => payload?.data ?? payload;

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${getJwt()}`,
    'Content-Type': 'application/json',
  }), [getJwt]);

  // ── Configured providers + available models ──────────────────────────────
  const [configuredProviders, setConfiguredProviders] = useState(null); // null = loading
  const [models, setModels] = useState([]); // models available to this tenant

  useEffect(() => {
    if (!appId) return;
    // Fetch configured providers for the warning banner check
    fetch(`${API_URL}/data/settings/api-keys`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        const providers = new Set((data.api_keys || []).map(k => k.provider));
        setConfiguredProviders(providers);
      })
      .catch(() => setConfiguredProviders(new Set()));
    // Fetch models scoped to this tenant's configured providers
    fetch(workflowReplayUrl('/replay-models'), { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => setModels(unwrapV2(data).models || []))
      .catch(() => setModels([]));
  }, [appId, workflowReplayUrl]); // eslint-disable-line

  // ── Workflow list state ───────────────────────────────────────────────────
  const [workflows, setWorkflows] = useState([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);

  useEffect(() => {
    if (!appId) return;
    let cancelled = false;
    setWorkflowsLoading(true);
    // Scoped to the app's global timeframe selector — the backend lists only
    // workflows with activity in this window, with stats computed for it, so the
    // list and its columns are always consistent with each other.
    const url = workflowReplayUrl('/workflows', { range: selectedRange, include_simulation_counts: 'true' });
    fetch(url, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        if (cancelled) return;
        const nextWorkflows = (unwrapV2(data) || []).map(w => ({
          ...w,
          workflow_name: w.workflow_name || w.canonical_name || w.workflow_id,
          avg_cost_usd: w.avg_cost_usd ?? 0,
          p50_latency_ms: w.p50_latency_ms ?? w.p50_ms ?? 0,
          signal_counts: w.signal_counts || {},
          models: w.models || [],
          simulation_run_count: w.simulation_run_count ?? 0,
        }));
        setWorkflows(nextWorkflows);
      })
      .catch(err => console.error('Failed to load workflows:', err))
      .finally(() => { if (!cancelled) setWorkflowsLoading(false); });
    return () => { cancelled = true; };
  }, [appId, environment, selectedRange, workflowReplayUrl]); // eslint-disable-line

  // ── Selected workflow + operation inventory ───────────────────────────────
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [operations, setOperations] = useState([]);
  const [operationsMeta, setOperationsMeta] = useState(null);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [representativeTrace, setRepresentativeTrace] = useState(null);

  const handleSelectWorkflow = useCallback((wf, options = {}) => {
    setSelectedWorkflow(wf);
    setView(options.view || 'simulate');
    setOperations([]);
    setOperationsMeta(null);
    setRepresentativeTrace(null);
    setOperationOverrides({});
    setCostEstimate(null);
    setRunStatus('idle');
    setRunResult(null);
    setRunHistory([]);
    setHistoryRunLoadingId(null);
    setOperationsLoading(true);
    const url = workflowReplayUrl(`/workflows/${wf.workflow_id}/llm-operations`);
    fetch(url, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        const payload = unwrapV2(data);
        setOperations(payload.operations || []);
        setOperationsMeta(payload.sample || null);
      })
      .catch(err => console.error('Failed to load LLM operations:', err))
      .finally(() => setOperationsLoading(false));

    fetch(workflowReplayUrl(`/workflows/${wf.workflow_id}/representative-trace`), { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        const payload = unwrapV2(data);
        setRepresentativeTrace(payload || null);
      })
      .catch(err => {
        console.error('Failed to load representative trace:', err);
        setRepresentativeTrace(null);
      });
  }, [appId, environment, authHeaders, workflowReplayUrl]); // eslint-disable-line

  // ── Run history ───────────────────────────────────────────────────────────
  const [runHistory, setRunHistory] = useState([]);
  const [historyRunLoadingId, setHistoryRunLoadingId] = useState(null);

  const fetchRunHistory = useCallback((workflowId) => {
    const url = workflowReplayUrl(`/workflows/${workflowId}/replay-runs`);
    fetch(url, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        const runs = unwrapV2(data).runs || [];
        setRunHistory(runs);
      })
      .catch(err => console.error('Failed to load run history:', err));
  }, [appId, authHeaders, workflowReplayUrl]); // eslint-disable-line

  useEffect(() => {
    if (selectedWorkflow) fetchRunHistory(selectedWorkflow.workflow_id);
  }, [selectedWorkflow]); // eslint-disable-line

  // ── Cost estimate ─────────────────────────────────────────────────────────
  const [costEstimate, setCostEstimate] = useState(null);

  // ── Operation overrides + sim state ───────────────────────────────────────
  const [operationOverrides, setOperationOverrides] = useState({});
  const [runStatus, setRunStatus] = useState('idle'); // 'idle' | 'running' | 'done' | 'error'
  const [runError, setRunError] = useState('');
  const [runResult, setRunResult] = useState(null);
  const [sampleSize, setSampleSize] = useState(1);
  // 'simulate' (config editor, no results) | 'results' (Simulation Results) | 'history' (past runs list)
  const [view, setView] = useState('simulate');

  const handleOpenWorkflowHistory = useCallback((wf) => {
    handleSelectWorkflow(wf, { view: 'history' });
  }, [handleSelectWorkflow]);

  const handleNewWorkflowSimulation = useCallback((wf) => {
    handleSelectWorkflow(wf, { view: 'simulate' });
  }, [handleSelectWorkflow]);

  // Auto-select workflow if workflowId param is present (e.g. navigating from Cost tab)
  useEffect(() => {
    const workflowId = searchParams.get('workflowId');
    if (workflowId && !selectedWorkflow && workflows.length > 0) {
      const wf = workflows.find(w => w.workflow_id === workflowId);
      if (wf) {
        handleSelectWorkflow(wf);
        const next = new URLSearchParams(searchParams);
        next.delete('workflowId');
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, workflows]); // eslint-disable-line

  const operationOverrideList = useMemo(() => Object.values(operationOverrides), [operationOverrides]);
  const overrideCount = operationOverrideList.length;
  const estimatedReplayLlmRuns = useMemo(() => {
    if (!overrideCount) return null;
    const sampledTraces = operationsMeta?.eligible_traces || operationsMeta?.requested_traces || 0;
    const selectedKeys = new Set(Object.keys(operationOverrides));
    const estimate = operations.reduce((sum, operation) => {
      if (!selectedKeys.has(operation.llm_operation_model_key)) return sum;
      const runsPerTrace = sampledTraces > 0
        ? (operation.llm_runs || 0) / sampledTraces
        : (operation.calls_per_trace?.p50 || 1);
      return sum + runsPerTrace * sampleSize;
    }, 0);
    return Math.max(1, Math.round(estimate));
  }, [operationOverrides, operations, operationsMeta, overrideCount, sampleSize]);

  // Cost estimate — debounced, fires on override or sampleSize change
  useEffect(() => {
    if (!selectedWorkflow || operationOverrideList.length === 0) {
      setCostEstimate(null);
      return;
    }
    const timer = setTimeout(() => {
      fetch(workflowReplayUrl(`/workflows/${selectedWorkflow.workflow_id}/replay-estimates`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          sample_size: sampleSize,
          operation_overrides: operationOverrideList,
        }),
      })
        .then(r => r.ok ? r.json() : Promise.reject(r.status))
        .then(data => setCostEstimate(unwrapV2(data)))
        .catch(() => setCostEstimate(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [operationOverrideList, sampleSize, selectedWorkflow, workflowReplayUrl]); // eslint-disable-line

  const handleOperationOverrideChange = useCallback((operationKey, override) => {
    setOperationOverrides(prev => ({ ...prev, [operationKey]: override }));
  }, []);

  const handleClearOperationOverride = useCallback((operationKey) => {
    setOperationOverrides(prev => { const n = { ...prev }; delete n[operationKey]; return n; });
  }, []);

  const handleRun = useCallback(async () => {
    if (!selectedWorkflow || overrideCount === 0) return;
    setRunStatus('running');
    setRunError('');
    setRunResult(null);
    const overrideList = operationOverrideList;
    try {
      const resp = await fetch(workflowReplayUrl(`/workflows/${selectedWorkflow.workflow_id}/replay-runs`), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          sample_size: sampleSize,
          variant_label: overrideList.map(o => `${o.agent_name || o.llm_span_name}: ${o.original_model} → ${o.model || 'custom'}`).join(' | '),
          operation_overrides: overrideList,
        }),
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setRunResult(unwrapV2(data));
      setRunStatus('done');
      setView('results');
      // Refresh run history
      fetchRunHistory(selectedWorkflow.workflow_id);
    } catch (err) {
      console.error('Replay failed:', err.message);
      setRunError(err.message || 'Simulation failed');
      setRunStatus('error');
    }
  }, [selectedWorkflow, operationOverrideList, overrideCount, sampleSize, appId, authHeaders, fetchRunHistory, workflowReplayUrl]); // eslint-disable-line

  const handleLoadSpanDetails = useCallback(async ({ traceIndex, spanId }) => {
    if (!selectedWorkflow || !runResult?.run_id) {
      throw new Error('Run details are not available yet');
    }
    const url = workflowReplayUrl(`/workflows/${selectedWorkflow.workflow_id}/replay-runs/${runResult.run_id}/span-result`);
    const separator = url.includes('?') ? '&' : '?';
    const params = new URLSearchParams({
      trace_index: String(traceIndex),
      span_id: spanId,
    });
    const resp = await fetch(`${url}${separator}${params.toString()}`, {
      headers: authHeaders(),
    });
    if (!resp.ok) {
      const errBody = await resp.json().catch(() => ({}));
      throw new Error(errBody.error || `HTTP ${resp.status}`);
    }
    return unwrapV2(await resp.json());
  }, [selectedWorkflow, runResult, workflowReplayUrl, authHeaders]);

  const handleOpenHistoryRun = useCallback(async (run) => {
    if (!selectedWorkflow || !run?.run_id) return;
    setHistoryRunLoadingId(run.run_id);
    setRunError('');
    try {
      const resp = await fetch(workflowReplayUrl(`/workflows/${selectedWorkflow.workflow_id}/replay-runs/${run.run_id}`), {
        headers: authHeaders(),
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${resp.status}`);
      }
      const detail = unwrapV2(await resp.json());
      setRunResult(detail);
      setRunStatus('done');
      setView('results');
    } catch (err) {
      console.error('Failed to load replay run detail:', err.message);
      setRunError(err.message || 'Failed to load run detail');
    } finally {
      setHistoryRunLoadingId(null);
    }
  }, [selectedWorkflow, workflowReplayUrl, authHeaders]);

  const handleBack = useCallback(() => {
    setSelectedWorkflow(null);
    setOperations([]);
    setOperationsMeta(null);
    setRepresentativeTrace(null);
    setOperationOverrides({});
    setRunStatus('idle');
    setRunError('');
    setRunResult(null);
    setCostEstimate(null);
    setRunHistory([]);
    setHistoryRunLoadingId(null);
    setView('simulate');
  }, []);

  // ── Workflow list ──
  if (!selectedWorkflow) {
    return (
      <WorkflowList
        workflows={workflows}
        loading={workflowsLoading}
        onSelect={handleOpenWorkflowHistory}
        onNewSimulation={handleNewWorkflowSimulation}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6" style={{ minHeight: 0 }}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-sm"
          style={{ color: palette.text.muted }}
          onMouseEnter={e => e.currentTarget.style.color = palette.text.primary}
          onMouseLeave={e => e.currentTarget.style.color = palette.text.muted}
        >
          <ArrowLeft size={14} /> Business Tasks
        </button>
        <span style={{ color: palette.borders.strong }}>/</span>
        <span className="text-sm font-semibold" style={{ color: palette.text.primary }}>{selectedWorkflow.workflow_name || selectedWorkflow.name}</span>
        <span className="text-xs" style={{ color: palette.text.muted }}>
          · {selectedWorkflow.trace_count} traces
          {operationsLoading
            ? ' · loading LLM operations…'
            : operationsMeta
              ? ` · ${operationsMeta.eligible_traces} sampled traces · ${operationsMeta.llm_runs} LLM runs`
              : ''}
        </span>
        <div className="flex items-center gap-2 ml-auto">
          {(view === 'results' || view === 'history') && (
            <button
              onClick={() => setView('simulate')}
              className="flex items-center gap-1.5 text-xs font-semibold"
              style={{ padding: '4px 10px', borderRadius: 6, border: 'none', color: palette.text.inverse, backgroundColor: palette.accents.primary.base }}
            >
              <Play size={10} fill="currentColor" /> New simulation
            </button>
          )}
          {view !== 'history' && (
            <button
              onClick={() => { setRunError(''); setView('history'); }}
              className="flex items-center gap-1.5 text-xs font-semibold"
              style={{
                padding: '4px 10px',
                borderRadius: 6,
                border: `1px solid ${palette.borders.subtle}`,
                color: getTableCellColor('muted'),
                backgroundColor: palette.neutrals.surfaceSubtle,
              }}
            >
              <Clock size={11} />History <ChevronRight size={13} />
            </button>
          )}
        </div>
      </div>

      {view === 'history' && (
        <RunHistoryPanel
          runs={runHistory}
          loadingRunId={historyRunLoadingId}
          onOpenRun={handleOpenHistoryRun}
          error={runError}
        />
      )}

      {view === 'simulate' && <>
        <div>
          <h2 className="text-base font-semibold" style={{ color: palette.text.heading }}>Simulate</h2>
          <p className="text-sm mt-1" style={{ color: palette.text.muted }}>
            Swap the model or settings for one or more LLM operations below, then replay this business task's historical traces to see the cost, latency, and output impact — without touching production.
          </p>
        </div>

        <OperationConfigPanel
          operations={operations}
          loading={operationsLoading}
          overrides={operationOverrides}
          models={models}
          configuredProviders={configuredProviders}
          onChange={handleOperationOverrideChange}
          onClear={handleClearOperationOverride}
          overrideCount={overrideCount}
          runStatus={runStatus}
          runError={runError}
          onRun={handleRun}
          sampleSize={sampleSize}
          onSampleSizeChange={setSampleSize}
          estimatedReplayLlmRuns={estimatedReplayLlmRuns}
          costEstimate={costEstimate}
          representativeTrace={representativeTrace}
          traceDetailUrl={traceDetailUrl}
        />
      </>}

      {view === 'results' && runResult && (
        <ResultsPanelV2
          workflow={selectedWorkflow}
          hasResults
          runResult={runResult}
          runStatus={runStatus}
          runError={runError}
          onLoadSpanDetails={handleLoadSpanDetails}
          operations={operations}
          operationsLoading={operationsLoading}
          representativeTrace={representativeTrace}
          configuredProviders={configuredProviders}
          isAdmin={isAdmin}
          traceDetailUrl={traceDetailUrl}
        />
      )}

    </div>
  );
};

export default SimulationStudio;
